import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '../../../shared/services/api.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { deleteObject, getStorage, ref, uploadBytes } from 'firebase/storage';
import * as XLSX from 'xlsx';
import { AuthService } from '../../../core/auth/auth.service';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { environment } from '../../../../environments/environment';
import { ConsentFlowService } from '../../../shared/services/consent-flow.service';

// South African ID number regex: 13 digits
const SA_ID_REGEX = /^[0-9]{13}$/;

interface UploadBatchEntry {
  rawValue: string;
  normalizedId: string | null;
}

@Component({
  selector: 'app-new-request',
  templateUrl: './new-request.component.html',
  styleUrls: ['./new-request.component.css']
})
export class NewRequestComponent implements OnInit {
  requestForm: FormGroup;
  readonly maxIdNumbers = 20;
  readonly defaultMaxRecords = 100;
  readonly maxUploadSizeBytes = 1024 * 1024;
  isSubmitting = false;
  isBusyOverlayVisible = false;
  selectedTabIndex = 0;
  uploadedFileName = '';
  uploadedFilePath = '';
  parsedUploadEntries: UploadBatchEntry[] = [];
  uploadParseStatusMessage = '';
  isUploadExampleVisible = false;
  private estimatedCostPerLookup: number | null = null;
  private estimatedMaxRowsAllowed: number | null = null;
  private hasLoadedEstimate = false;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private consentFlowService: ConsentFlowService
  ) {
    this.requestForm = this.fb.group({
      idNumbers: this.fb.array([this.fb.control('', this.saIdValidator)]),
      maxRecords: [this.defaultMaxRecords, [Validators.required, Validators.min(1), Validators.max(1000)]]
    });
  }

  ngOnInit(): void {
    this.showPostLoginBillingNoticeIfNeeded();
    this.preloadUsageEstimate();
  }

  private showPostLoginBillingNoticeIfNeeded(): void {
    const rawNotice = String(this.route.snapshot.queryParamMap.get('billingNotice') || '')
      .trim()
      .toLowerCase();
    if (rawNotice !== 'payment-required') {
      return;
    }

    this.snackBar.open('Payment failed. Complete payment under Account Info.', 'Close', {
      duration: 7000,
    });

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { billingNotice: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  get idNumbers(): FormArray {
    return this.requestForm.get('idNumbers') as FormArray;
  }

  saIdValidator(control: any): { [key: string]: any } | null {
    if (!control.value) {
      return null; // Optional field
    }
    const value = control.value.toString().trim();
    if (value && !SA_ID_REGEX.test(value)) {
      return { invalidId: true };
    }
    return null;
  }

  get hasAnyIdNumber(): boolean {
    return this.getNonEmptyIdValues().length > 0;
  }

  get showMaxRecordsField(): boolean {
    return false;
  }

  addIdField(): void {
    if (this.idNumbers.length >= this.maxIdNumbers) {
      this.snackBar.open(`Maximum of ${this.maxIdNumbers} ID numbers allowed`, 'Close', { duration: 3000 });
      return;
    }
    this.idNumbers.push(this.fb.control('', this.saIdValidator));
  }

  removeIdField(index: number): void {
    if (this.idNumbers.length > 1) {
      this.idNumbers.removeAt(index);
    }
  }

  onIdPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    if (!clipboardData) {
      return;
    }
    const text = clipboardData.getData('text') || '';
    if (!text) {
      return;
    }

    event.preventDefault();

    // Split by newlines, then by whitespace, to handle Excel column pastes
    const lines = text.split(/\r?\n/);
    const allTokens: string[] = [];
    
    for (const line of lines) {
      // Split by whitespace (spaces, tabs) and add each token
      const tokens = line.split(/\s+/).filter((t: string) => t.trim().length > 0);
      allTokens.push(...tokens);
    }

    // Extract valid 13-digit ID numbers
    const validIds: string[] = [];
    for (const token of allTokens) {
      const trimmed = token.trim();
      // Check if it's exactly 13 digits
      if (/^\d{13}$/.test(trimmed)) {
        validIds.push(trimmed);
        if (validIds.length >= this.maxIdNumbers) break;
      }
    }

    if (!validIds.length) {
      this.snackBar.open('No valid 13-digit ID numbers found in pasted data', 'Close', { duration: 3000 });
      return;
    }

    // Populate the FormArray with parsed IDs (keep all, even duplicates)
    this.idNumbers.clear();
    validIds.forEach((id) => {
      this.idNumbers.push(this.fb.control(id, this.saIdValidator));
    });
  }

  async onSubmit(): Promise<void> {
    const idValues = this.getNonEmptyIdValues();
    if (idValues.length === 0) {
      this.snackBar.open('At least one valid ID Number is required', 'Close', { duration: 3000 });
      return;
    }
    const capped = this.applyEstimatedCreditCap(idValues);
    if (capped.allowed.length === 0) {
      return;
    }

    if (!this.requestForm.valid) {
      this.snackBar.open('Please fill in all required fields correctly', 'Close', { duration: 3000 });
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    this.isBusyOverlayVisible = true;
    const payloadBase: any = {
      maxRecords: this.showMaxRecordsField ? this.requestForm.value.maxRecords : this.defaultMaxRecords
    };
    const submittedCount = capped.allowed.length;
    this.analyticsService.trackEvent('request_submit_started', {
      request_count: submittedCount,
      source: 'NEW_REQUEST'
    });
    const successfulRequestIds: string[] = [];
    const failedById: Array<{ idNumber: string; reason: string }> = [];

    try {
      if (submittedCount > 1) {
        await this.submitMultiSearch(capped.allowed, payloadBase.maxRecords, 'NEW_REQUEST');
        return;
      }

      for (const idNumber of capped.allowed) {
        try {
          const response = await firstValueFrom(
            this.apiService.createRequest({
              ...payloadBase,
              idNumber
            })
          );
          successfulRequestIds.push(response.requestId);
        } catch (error: any) {
          failedById.push({
            idNumber,
            reason: this.toCreateRequestErrorMessage(error),
          });
        }
      }

      if (successfulRequestIds.length === 0) {
        this.snackBar.open(
          'There was an error processing your request',
          'Close',
          { duration: 7000 }
        );
        return;
      }

      if (submittedCount === 1 && successfulRequestIds.length === 1) {
        const consentAccepted = await this.ensureConsentAcceptedForDataAccess();
        if (!consentAccepted) {
          return;
        }
        this.analyticsService.trackEvent('request_submit_succeeded', {
          request_count: 1,
          source: 'NEW_REQUEST'
        });
        const from = this.isUserRouteContext() ? 'user-request' : 'new-request';
        await this.router.navigate(['/admin/data', successfulRequestIds[0]], {
          queryParams: { from }
        });
        return;
      }
    } catch (error) {
      this.analyticsService.trackEvent('request_submit_failed', {
        source: 'NEW_REQUEST'
      });
      console.error('Failed to submit ID requests:', error);
      this.snackBar.open('An unexpected error occurred while submitting requests', 'Close', { duration: 5000 });
    } finally {
      this.isBusyOverlayVisible = false;
      this.isSubmitting = false;
    }
  }

  async onUploadFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.uploadedFileName = file.name;
    this.uploadedFilePath = '';
    this.parsedUploadEntries = [];
    this.uploadParseStatusMessage = 'Processing file...';

    try {
      this.validateUploadFile(file);
      await this.validateFileSignature(file);
      await this.uploadFileToStorage(file);
      this.parsedUploadEntries = await this.extractUploadEntriesFromFile(file);
      if (this.parsedUploadEntries.length < 1) {
        await this.deleteUploadedFile();
        this.uploadParseStatusMessage = 'No non-empty rows were found. Please upload a different file.';
        this.snackBar.open('Upload must contain at least one non-empty row', 'Close', { duration: 5000 });
        return;
      }
      const validCount = this.parsedUploadEntries.filter((entry) => !!entry.normalizedId).length;
      this.analyticsService.trackEvent('upload_ids_parsed', {
        id_count: validCount,
        row_count: this.parsedUploadEntries.length,
        file_extension: this.getFileExtension(file.name)
      });
      this.uploadParseStatusMessage = 'File parsed successfully. Click Search to begin.';
    } catch (error: any) {
      await this.deleteUploadedFile();
      this.uploadedFilePath = '';
      this.parsedUploadEntries = [];
      this.uploadParseStatusMessage = 'File parsing failed. Please upload a valid CSV or XLSX file.';
      this.snackBar.open(error?.message || 'Failed to process upload file', 'Close', { duration: 6000 });
    } finally {
      input.value = '';
    }
  }

  toggleUploadExample(): void {
    this.isUploadExampleVisible = !this.isUploadExampleVisible;
  }

  async submitUploadedIds(): Promise<void> {
    if (this.parsedUploadEntries.length < 1) {
      this.snackBar.open('Upload a file containing at least one non-empty row first', 'Close', { duration: 5000 });
      return;
    }
    if (this.isSubmitting) {
      return;
    }

    const rawValues = this.parsedUploadEntries.map((entry) => entry.rawValue);
    const capped = this.applyEstimatedCreditCap(rawValues);
    if (capped.allowed.length === 0) {
      return;
    }
    this.isSubmitting = true;
    this.isBusyOverlayVisible = true;
    this.analyticsService.trackEvent('upload_submit_started', {
      row_count: capped.allowed.length,
      valid_id_count: this.parsedUploadEntries.filter((entry) => !!entry.normalizedId).length
    });
    try {
      await this.submitMultiSearch(capped.allowed, this.defaultMaxRecords, 'CSV_UPLOAD');
    } catch (error: any) {
      this.snackBar.open(
        error?.message || 'An unexpected error occurred while submitting uploaded IDs',
        'Close',
        { duration: 5000 }
      );
    } finally {
      await this.deleteUploadedFile();
      this.uploadedFileName = '';
      this.uploadedFilePath = '';
      this.parsedUploadEntries = [];
      this.uploadParseStatusMessage = '';
      this.isBusyOverlayVisible = false;
      this.isSubmitting = false;
    }
  }

  private async ensureConsentAcceptedForDataAccess(): Promise<boolean> {
    // Hide loading overlay while user interacts with POPIA modal.
    this.isBusyOverlayVisible = false;
    const consentAccepted = await this.consentFlowService.ensureConsentAccepted();
    this.isBusyOverlayVisible = true;
    return consentAccepted;
  }

  private toCreateRequestErrorMessage(error: any): string {
    let errorMessage = 'Failed to create request';
    const reasonCode = error?.error?.error?.reasonCode;
    if (reasonCode === 'INSUFFICIENT_CREDITS') {
      return 'You have no credits remaining. Please contact an administrator';
    }
    if (reasonCode === 'INSUFFICIENT_USER_CREDITS') {
      return 'You do not have enough credits for this request';
    }
    if (error?.error?.error?.message) {
      errorMessage = error.error.error.message;
    }
    return errorMessage;
  }

  private getNonEmptyIdValues(): string[] {
    return this.idNumbers.controls
      .map((c) => (c.value || '').toString().trim())
      .filter((v) => !!v);
  }

  private preloadUsageEstimate(): void {
    this.apiService.getBillingUsageSummary().subscribe({
      next: (summary) => {
        const estimate = Number(summary?.estimation?.estimatedCostPerLookup || 0);
        const maxRowsAllowed = Number(summary?.estimation?.maxRowsAllowed);
        if (Number.isFinite(estimate) && estimate > 0) {
          this.estimatedCostPerLookup = Math.max(1, Math.trunc(estimate));
        } else {
          this.estimatedCostPerLookup = null;
        }
        if (Number.isFinite(maxRowsAllowed) && maxRowsAllowed >= 0) {
          this.estimatedMaxRowsAllowed = Math.max(0, Math.trunc(maxRowsAllowed));
        } else {
          this.estimatedMaxRowsAllowed = null;
        }
        this.hasLoadedEstimate = true;
      },
      error: () => {
        this.estimatedCostPerLookup = null;
        this.estimatedMaxRowsAllowed = null;
        this.hasLoadedEstimate = true;
      },
    });
  }

  private applyEstimatedCreditCap(idValues: string[]): { allowed: string[]; skipped: string[] } {
    if (!this.hasLoadedEstimate || this.estimatedCostPerLookup == null || this.estimatedMaxRowsAllowed == null) {
      return { allowed: idValues, skipped: [] };
    }
    if (idValues.length <= this.estimatedMaxRowsAllowed) {
      return { allowed: idValues, skipped: [] };
    }
    const allowed = idValues.slice(0, this.estimatedMaxRowsAllowed);
    const skipped = idValues.slice(this.estimatedMaxRowsAllowed);
    if (allowed.length === 0) {
      this.snackBar.open(
        `You have insufficient credits for this request.`,
        'Close',
        { duration: 7000 }
      );
      return { allowed, skipped };
    }
    this.snackBar.open(
      `You have insufficient credits. Submitted first ${allowed.length} row${allowed.length === 1 ? '' : 's'}.`,
      'Close',
      { duration: 7000 }
    );
    return { allowed, skipped };
  }

  private async submitMultiSearch(
    idValues: string[],
    maxRecords: number,
    source: 'NEW_REQUEST' | 'CSV_UPLOAD'
  ): Promise<void> {
    const multiResponse = await firstValueFrom(
      this.apiService.createMultiRequest({
        idNumbers: idValues,
        maxRecords,
        source,
      })
    );
    this.analyticsService.trackEvent('multi_search_created', {
      source,
      total_inputs: multiResponse.totalInputs,
      submitted_count: multiResponse.submittedCount,
      failed_count: multiResponse.failedCount
    });
    const targetBase = this.isUserRouteContext() ? '/user/multi-search' : '/admin/multi-search';
    void this.router.navigate([targetBase, multiResponse.requestBatchId]);
  }

  private async submitSingleSearch(idNumber: string, maxRecords: number): Promise<void> {
    const response = await firstValueFrom(
      this.apiService.createRequest({
        idNumber,
        maxRecords,
      })
    );
    this.analyticsService.trackEvent('single_search_created', {
      source: this.isUserRouteContext() ? 'USER_REQUEST' : 'NEW_REQUEST'
    });
    const consentAccepted = await this.ensureConsentAcceptedForDataAccess();
    if (!consentAccepted) {
      return;
    }
    const from = this.isUserRouteContext() ? 'user-request' : 'new-request';
    await this.router.navigate(['/admin/data', response.requestId], {
      queryParams: { from }
    });
  }

  private validateUploadFile(file: File): void {
    if (file.size > this.maxUploadSizeBytes) {
      throw new Error('File size exceeds 1MB limit');
    }

    const extension = this.getFileExtension(file.name);
    if (!['csv', 'xlsx'].includes(extension)) {
      throw new Error('Only CSV and XLSX files are allowed');
    }

    const csvMimeTypes = ['text/csv', 'application/csv', 'text/plain', 'application/vnd.ms-excel', ''];
    const xlsxMimeTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ''];
    const allowedTypes = extension === 'csv' ? csvMimeTypes : xlsxMimeTypes;
    if (!allowedTypes.includes(file.type)) {
      throw new Error('File type does not match the expected format');
    }
  }

  private async validateFileSignature(file: File): Promise<void> {
    const extension = this.getFileExtension(file.name);
    const headerBytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
    if (headerBytes.length === 0) {
      throw new Error('Uploaded file is empty');
    }

    if (extension === 'xlsx') {
      const isZipHeader = headerBytes[0] === 0x50 && headerBytes[1] === 0x4b;
      if (!isZipHeader) {
        throw new Error('Invalid XLSX file signature');
      }
      return;
    }

    const hasNullByte = headerBytes.some((byte) => byte === 0);
    if (hasNullByte) {
      throw new Error('CSV file appears to be binary and was rejected');
    }
  }

  private async uploadFileToStorage(file: File): Promise<void> {
    const currentUser = await firstValueFrom(this.authService.getCurrentUser());
    if (!currentUser?.uid) {
      throw new Error('Unable to validate authenticated user for file upload');
    }

    const extension = this.getFileExtension(file.name);
    const safeFileName = this.sanitizeFileName(file.name);
    const storagePath = `user_uploads/${environment.tenantKey}/${currentUser.uid}/${Date.now()}-${safeFileName}`;
    const storageRef = ref(getStorage(), storagePath);
    const contentType = extension === 'xlsx'
      ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : 'text/csv';
    await uploadBytes(storageRef, file, {
      contentType,
      customMetadata: {
        uploadSource: 'new-request-file-upload',
      },
    });
    this.uploadedFilePath = storagePath;
  }

  private async deleteUploadedFile(): Promise<void> {
    if (!this.uploadedFilePath) {
      return;
    }

    try {
      const storageRef = ref(getStorage(), this.uploadedFilePath);
      await deleteObject(storageRef);
    } catch (error) {
      console.warn('Failed to delete uploaded file from storage:', error);
    }
  }

  private async extractUploadEntriesFromFile(file: File): Promise<UploadBatchEntry[]> {
    const extension = this.getFileExtension(file.name);
    const rawValues = extension === 'xlsx'
      ? await this.extractEntriesFromXlsx(file)
      : await this.extractEntriesFromCsv(file);

    if (rawValues.length > this.maxIdNumbers) {
      this.snackBar.open(`Only the first ${this.maxIdNumbers} non-empty rows will be used`, 'Close', { duration: 5000 });
    }
    return rawValues.slice(0, this.maxIdNumbers).map((rawValue) => ({
      rawValue,
      normalizedId: this.normalizeSaId(rawValue),
    }));
  }

  private async extractEntriesFromCsv(file: File): Promise<string[]> {
    const text = await file.text();
    return this.extractEntriesFromText(text);
  }

  private async extractEntriesFromXlsx(file: File): Promise<string[]> {
    const bytes = await file.arrayBuffer();
    const workbook = XLSX.read(bytes, { type: 'array', raw: false });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      return [];
    }
    const sheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      raw: false,
      blankrows: true,
      defval: '',
    });
    return rows.flatMap((row) =>
      row
        .map((value) => `${value ?? ''}`)
        .filter((value) => value.trim().length > 0)
    );
  }

  private extractEntriesFromText(text: string): string[] {
    const entries: string[] = [];
    const lines = text.split(/\r?\n/);
    for (const line of lines) {
      const tokens = line.split(/[,;\t]/);
      for (const token of tokens) {
        if (token.trim().length > 0) {
          entries.push(token);
        }
      }
    }
    return entries;
  }

  private normalizeSaId(value: string): string | null {
    const digits = value.replace(/\D/g, '');
    return SA_ID_REGEX.test(digits) ? digits : null;
  }

  private sanitizeFileName(fileName: string): string {
    const trimmed = fileName.trim().replace(/\s+/g, '_');
    return trimmed.replace(/[^a-zA-Z0-9._-]/g, '');
  }

  private getFileExtension(fileName: string): string {
    const parts = fileName.toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
  }

  private isUserRouteContext(): boolean {
    return this.router.url.startsWith('/user/');
  }
}
